import React, { useEffect, useState } from "react";
import axios from "axios";
import { Copy } from "lucide-react";

export default function OrdersAdminNew() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState(false); 

  // ✅ Fetch all New Orders
  const fetchOrders = async () => {
    try {
      const res = await axios.get("https://api.speedfollowinfo.com/api/admin/new-orders");
      if (res.data.success) setOrders(res.data.orders);
    } catch (err) {
      console.error("❌ Fetch Orders Error:", err);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ✅ Copy input text
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    const msg = document.createElement("div");
    msg.textContent = "📋 Copied!";
    msg.style.position = "fixed";
    msg.style.top = "20px";
    msg.style.right = "20px";
    msg.style.background = "#4ade80";
    msg.style.color = "#0b0f1a";
    msg.style.fontWeight = "600";
    msg.style.padding = "10px 18px";
    msg.style.borderRadius = "8px";
    msg.style.boxShadow = "0 0 15px rgba(0,0,0,0.5)";
    msg.style.zIndex = "9999";
    document.body.appendChild(msg);
    setTimeout(() => msg.remove(), 1500);
  };

  // ✅ Update Order Status
  const handleStatusUpdate = async (orderId) => {
  if (!selectedStatus) return alert("Please select a status!");

  // ✅ Prevent multiple clicks
  if (loading) return;

  if (selectedStatus === "Complete" && !selectedFile && !adminNote.trim()) {
    alert("⚠️ Please add a note or upload a file before marking as Complete!");
    return;
  }

  try {
    setLoading(true); // ✅ start processing

    const formData = new FormData();
    formData.append("status", selectedStatus);
    if (selectedFile) formData.append("file", selectedFile);
    if (adminNote) formData.append("note", adminNote);

    const res = await axios.put(
      `https://api.speedfollowinfo.com/api/admin/order-status/${orderId}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    if (res.data.success) {
      if (["Complete", "Cancelled"].includes(selectedStatus)) {
        setOrders((prev) => prev.filter((o) => o._id !== orderId));
      } else {
        setOrders((prev) =>
          prev.map((o) =>
            o._id === orderId ? { ...o, status: selectedStatus } : o
          )
        );
      }

      setSelectedOrder(null);
      setSelectedStatus("");
      setSelectedFile(null);
      setAdminNote("");

      // ✅ Toast
      const msg = document.createElement("div");
      msg.textContent = `✅ ${selectedStatus} Successfully!`;
      msg.style.position = "fixed";
      msg.style.top = "20px";
      msg.style.right = "20px";
      msg.style.background =
        selectedStatus === "Cancelled" ? "#f87171" : "#4ade80";
      msg.style.color = "#0b0f1a";
      msg.style.fontWeight = "600";
      msg.style.padding = "10px 18px";
      msg.style.borderRadius = "8px";
      msg.style.boxShadow = "0 0 15px rgba(0,0,0,0.5)";
      msg.style.zIndex = "9999";
      document.body.appendChild(msg);
      setTimeout(() => msg.remove(), 2000);
    }
  } catch (err) {
    console.error("⚠️ Status Update Error:", err);
    alert("⚠️ Failed to update status!");
  } finally {
    setLoading(false); // ✅ end processing
  }
};

  // ✅ Function: Count total orders by user
  const countOrdersByUser = (username) => {
    return orders.filter((o) => o.user?.username === username).length;
  };

  return (
    <div className="p-6 text-white">
    {/* ✅ Loading Overlay */}
{loading && (selectedStatus === "Complete" || selectedStatus === "Cancelled") && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
      <p className="text-black font-semibold">Processing...</p>
    </div>
  </div>
)}
      <h2 className="text-2xl font-bold mb-6 text-[#f2a900]">
        🛠️ Manage New Orders
      </h2>

      <div className="overflow-x-auto rounded-lg border border-gray-800">
        <table className="w-full text-sm text-left">
          <thead className="bg-[#111827] text-[#f2a900] uppercase">
            <tr>
              <th className="p-3">#</th>
              <th className="p-3">Username</th>
              <th className="p-3">Category & Input</th>
              <th className="p-3">Quantity</th>
              <th className="p-3">Price</th>
              <th className="p-3">Status</th>
              <th className="p-3">Date</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="text-center p-5 text-gray-400 italic"
                >
                  No new orders found
                </td>
              </tr>
            ) : (
              orders.map((order) => {
                const username = order.user?.username || "Unknown User";
                const totalOrders = countOrdersByUser(username);

                return (
                  <tr
                    key={order._id}
                    className="border-b border-gray-800 hover:bg-[#1f2937]"
                  >
                    <td className="p-3 text-gray-400">#{order.orderNumber || totalOrders}</td>
                    <td className="p-3 font-semibold">{username}</td>

                    {/* ✅ Category + Input সুন্দরভাবে */}
                    <td className="p-3">
                      <div className="bg-[#0f172a] rounded-lg border border-gray-700 p-2 flex items-center justify-between">
                        <div>
                          <div className="text-[#f2a900] font-medium">
                              {order.service || "No Service"}
                             </div>
                          <div className="text-gray-300 text-xs mt-1">
                            {order.info || "No Input"}
                          </div>
                        </div>
                        {order.info && (
                          <button
                            onClick={() => handleCopy(order.info)}
                            className="ml-2 text-gray-400 hover:text-[#f2a900]"
                            title="Copy input"
                          >
                            <Copy size={16} />
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="p-3">{order.quantity}</td>
                    <td className="p-3 text-[#ffd700] font-semibold">
                      ৳{order.price}
                    </td>
                    <td
                      className={`p-3 font-semibold ${
                        order.status === "Complete"
                          ? "text-green-400"
                          : order.status === "Cancelled"
                          ? "text-red-400"
                          : "text-yellow-400"
                      }`}
                    >
                      {order.status}
                    </td>
                    <td className="p-3 text-gray-400">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>

                    <td className="p-3 text-center">
                      {selectedOrder === order._id ? (
                        <div className="flex flex-col items-center gap-2">
                          <select
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="bg-[#0b0f1a] text-white border border-gray-500 rounded p-1"
                          >
                            <option value="">Select</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Complete">Complete</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>

                          {(selectedStatus === "Complete" ||
                            selectedStatus === "Cancelled") && (
                            <textarea
                              placeholder={
                                selectedStatus === "Cancelled"
                                  ? "Write reason..."
                                  : "Write note..."
                              }
                              onChange={(e) => setAdminNote(e.target.value)}
                              className="bg-[#111827] text-white p-2 rounded w-full text-xs"
                            ></textarea>
                          )}

                          {selectedStatus === "Complete" && (
                            <input
                              type="file"
                              accept=".jpg,.png,.pdf"
                              onChange={(e) => setSelectedFile(e.target.files[0])}
                              className="text-xs text-gray-400"
                            />
                          )}

                          <button
                            onClick={() => handleStatusUpdate(order._id)}
                            className="bg-[#f2a900] text-black font-bold px-3 py-1 rounded hover:bg-[#e0a800]"
                          >
                            OK
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedOrder(order._id)}
                          className="bg-[#3b82f6] text-white font-semibold px-3 py-1 rounded hover:bg-[#2563eb]"
                        >
                          Set Status
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}